import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment, useMutation } from 'react-relay';
import { Transition } from 'react-transition-group';
import { type TransitionStatus, ENTERED, EXITED } from 'react-transition-group/Transition';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import Shortcuts from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcuts/index.tsx';
import { FormattedMessage } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	useSidebarActions,
	useSidebarStore,
} from '@atlassian/jira-software-releases-issue-sidebar-use-sidebar-store/src/controllers/index.tsx';
import {
	RIGHT_SIDEBAR_WIDTH,
	VERSION_DETAIL_PADDING,
} from '@atlassian/jira-software-releases-version-detail-shared/src/common/constants.tsx';
import { SIDEBAR_RESIZE_BUTTON_SELECTOR } from '../../common/constants.tsx';
import type {
	rightSidebar_softwareReleasesVersionDetail_updateCollapsedUisMutation,
	rightSidebar_softwareReleasesVersionDetail_updateCollapsedUisMutation$data,
} from './__generated__/rightSidebar_softwareReleasesVersionDetail_updateCollapsedUisMutation.graphql';
import type {
	JiraVersionDetailsCollapsedUi,
	rightSidebar_softwareReleasesVersionDetail$key,
} from './__generated__/rightSidebar_softwareReleasesVersionDetail.graphql';
import { TRANSITION_DURATION } from './constants.tsx';
import { messages } from './messages.tsx';
import { ResizeBoundary, ResizeButton } from './resize-control/ResizeButton.tsx';

const getNewCollapsedUis = (
	currentState: JiraVersionDetailsCollapsedUi[],
	isCurrentlyCollapsed: boolean,
): JiraVersionDetailsCollapsedUi[] =>
	isCurrentlyCollapsed
		? currentState.filter((value) => value !== 'RIGHT_SIDEBAR')
		: [...currentState, 'RIGHT_SIDEBAR'];

type Props = {
	jiraVersion: rightSidebar_softwareReleasesVersionDetail$key;
	children: ReactNode;
};

export const RightSidebar = ({ jiraVersion, children }: Props): React.JSX.Element => {
	const [state] = useSidebarStore();
	const sidebarActions = useSidebarActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment rightSidebar_softwareReleasesVersionDetail on JiraVersion {
				collapsedUis @optIn(to: ["JiraVersionCollapsedUis"])
				id @required(action: THROW)
			}
		`,
		jiraVersion,
	);

	const [updateCollapsedUis] =
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		useMutation<rightSidebar_softwareReleasesVersionDetail_updateCollapsedUisMutation>(graphql`
			mutation rightSidebar_softwareReleasesVersionDetail_updateCollapsedUisMutation(
				$input: JiraVersionDetailsCollapsedUisInput!
			) @raw_response_type {
				jira {
					saveVersionDetailsCollapsedUis(input: $input)
						@optIn(to: ["JiraSaveVersionDetailsCollapsedUis", "JiraVersionCollapsedUis"]) {
						success
						version {
							collapsedUis
						}
					}
				}
			}
		`);

	const onError = () => {
		fireUIAnalytics({
			// @ts-expect-error - Argument of type '{ meta: { id: string; packageName: string; teamName: string; }; error: Error; sendToPrivacyUnsafeSplunk: boolean; }' is not assignable to parameter of type 'UIAnalyticsEvent'.
			meta: {
				id: 'jira.releases.version.detail.collapsed-uis.user-preference.mutation.error.id',
				packageName: 'jiraSoftwareReleasesVersionDetail',
				teamName: 'jolt',
			},
			error: Error('Unable to update collapsed ui state'),
			sendToPrivacyUnsafeSplunk: true,
		});
	};
	const saveUserPreference = (newCollapsedUis: JiraVersionDetailsCollapsedUi[]) => {
		updateCollapsedUis({
			variables: {
				input: {
					collapsedUis: newCollapsedUis,
					versionId: version.id,
				},
			},
			onCompleted: (
				data: rightSidebar_softwareReleasesVersionDetail_updateCollapsedUisMutation$data,
			) => {
				if (!data.jira?.saveVersionDetailsCollapsedUis?.success) {
					onError();
				}
			},
			onError,
			optimisticResponse: {
				jira: {
					saveVersionDetailsCollapsedUis: {
						success: true,
						version: {
							collapsedUis: newCollapsedUis,
							id: version.id,
						},
					},
				},
			},
		});
	};

	const collapsedUis = (version.collapsedUis ?? []).filter(Boolean);

	const isIssueClosed = state.status === 'CLOSED';
	const isSidebarExpanded = !collapsedUis.includes('RIGHT_SIDEBAR') || !isIssueClosed;

	const keyboardShortcutPressedEvent = createAnalyticsEvent({
		action: 'pressed',
		actionSubject: 'keyboardShortcut',
	});
	const buttonClickedEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'button',
	});

	const handleChange = (analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'versionDetailsCollapsibleRightSidebar', {
			targetState: isSidebarExpanded ? 'collapsed' : 'expanded',
		});
		if (!isIssueClosed) {
			sidebarActions.close();
		}
		saveUserPreference(getNewCollapsedUis(collapsedUis, !isSidebarExpanded));
	};

	return (
		<Transition in={isSidebarExpanded} timeout={100}>
			{(transitionState: TransitionStatus) => (
				<SidebarContainer isIssueClosed={isIssueClosed} transitionState={transitionState}>
					<Shortcuts
						keyMap={{
							']': {
								callback: () => handleChange(keyboardShortcutPressedEvent),
								label: <FormattedMessage {...messages.resizeKeyboardShortcutLabel} />,
							},
						}}
					/>
					<ResizeBoundary
						isSidebarCollapsed={!isSidebarExpanded}
						onClick={() => handleChange(buttonClickedEvent)}
					/>
					<ResizeButton
						isSidebarCollapsed={!isSidebarExpanded}
						onClick={() => handleChange(buttonClickedEvent)}
					/>
					<SidebarContent isIssueClosed={isIssueClosed} transitionState={transitionState}>
						{children}
					</SidebarContent>
				</SidebarContainer>
			)}
		</Transition>
	);
};

type SidebarProps = {
	isIssueClosed: boolean;
	transitionState: TransitionStatus;
};

const enteredTransitionContainerStyle = {
	flex: `0 0 ${RIGHT_SIDEBAR_WIDTH}px`,
	width: `${RIGHT_SIDEBAR_WIDTH}px`,
};

const exitedTransitionContainerStyle = {
	flex: '0 0 0',
	width: `${VERSION_DETAIL_PADDING}px`,
};

const enteredTransitionContentStyle = {
	opacity: 1,
};

const exitedTransitionContentStyle = {
	opacity: 0,
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SidebarContainer = styled.div<SidebarProps>(
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		flex: `0 0 ${RIGHT_SIDEBAR_WIDTH}px`,
		oveflowY: 'visible',
		position: 'relative',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		width: ({ isIssueClosed }) =>
			isIssueClosed
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${RIGHT_SIDEBAR_WIDTH}px`
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${RIGHT_SIDEBAR_WIDTH + VERSION_DETAIL_PADDING * 2}px`,
		transition: `
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766 */ ''}
            flex ${TRANSITION_DURATION}ms,
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766 */ ''}
            width ${TRANSITION_DURATION}ms
        `,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		[`&:hover [data-component-selector='${SIDEBAR_RESIZE_BUTTON_SELECTOR}']`]: {
			opacity: 1,
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	({ transitionState }) => transitionState === ENTERED && enteredTransitionContainerStyle,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	({ transitionState }) => transitionState === EXITED && exitedTransitionContainerStyle,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SidebarContent = styled.div<SidebarProps>(
	{
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		paddingTop: `${VERSION_DETAIL_PADDING}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		paddingBottom: `${VERSION_DETAIL_PADDING}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		paddingLeft: ({ isIssueClosed }) => (isIssueClosed ? `${VERSION_DETAIL_PADDING}px` : '0px'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		paddingRight: ({ isIssueClosed }) => (isIssueClosed ? `${VERSION_DETAIL_PADDING}px` : '0px'),
		maxHeight: '100%',
		height: '100%',
		boxSizing: 'border-box',
		overflowY: 'auto',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		transition: `opacity ${TRANSITION_DURATION}ms`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	({ transitionState }) => transitionState === ENTERED && enteredTransitionContentStyle,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	({ transitionState }) => transitionState === EXITED && exitedTransitionContentStyle,
);
